import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtCurrency, csvCell } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, MessageSquare } from "lucide-react";

const fmtDT = (v) => (v ? new Date(v).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const fmtD = (v) => (v ? new Date(`${String(v).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const stageLabel = (s) => (s || "").replace(/_/g, " ");

const CONFIG = {
  new_patients: {
    title: "New Patients (MTD)",
    desc: "Patients whose first visit that actually took place (an attended appointment or a DrChrono billing date) was this month.",
    cols: [["name", "Patient"], ["phone", "Phone"], ["first_visit", "First visit", fmtD], ["source", "Found in"], ["lifetime_value", "Lifetime value", (v) => fmtCurrency(v || 0)]],
  },
  no_shows: {
    title: "No-Shows · last 90 days",
    desc: "Appointments marked No Show in DrChrono over the last 90 days.",
    cols: [["datetime", "Appointment", fmtDT], ["name", "Patient"], ["phone", "Phone"], ["provider", "Provider"], ["reason", "Visit type"],
      ["rebooked_for", "Rebooked", (v, r) => (v ? `✓ ${fmtDT(v)}` : r?.rebook_text_sent_at ? "Not yet" : "—")]],
  },
  lead_conversion: {
    title: "Lead Conversion",
    desc: "Every lead in the pipeline. Converted leads are listed first. Conversion = converted ÷ all leads.",
    cols: [["name", "Lead"], ["source", "Source"], ["stage", "Stage", (s) => (s === "converted" ? "✓ Converted" : stageLabel(s))], ["created_at", "Created", fmtD], ["value", "Value", (v) => fmtCurrency(v || 0)]],
  },
};

function Summary({ kind, data }) {
  if (kind === "no_shows") {
    const rate = data.texted ? Math.round((data.rebooked / data.texted) * 100) : 0;
    return (
      <span className="flex flex-wrap items-center gap-1.5">
        <span>{data.rows.length} no-shows out of {data.past_appointments_90d} finished visits</span>
        <span data-testid="rebook-payoff" className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
          {data.texted} texted · {data.rebooked} rebooked{data.texted ? ` (${rate}%)` : ""}
        </span>
      </span>
    );
  }
  if (kind === "lead_conversion") {
    return (
      <span className="flex flex-wrap gap-1.5">
        <b>{data.converted} of {data.total} converted</b>
        {Object.entries(data.by_stage).map(([s, n]) => (
          <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{stageLabel(s)} {n}</span>
        ))}
      </span>
    );
  }
  return <span>{data.rows.length} new patients</span>;
}

export function RebookButton({ row }) {
  const qc = useQueryClient();
  const [sentAt, setSentAt] = useState(row.rebook_text_sent_at);
  const send = useMutation({
    mutationFn: async () => (await api.post(`/appointments/${row.appointment_id}/rebook-text`)).data,
    onSuccess: (d) => { setSentAt(d.sent_at); toast.success(`Rebook text sent to ${row.name}`); qc.invalidateQueries({ queryKey: ["kpi-detail", "no_shows"] }); qc.invalidateQueries({ queryKey: ["appointments"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Text failed"),
  });
  if (sentAt) {
    return <span data-testid={`rebook-sent-${row.appointment_id}`} className="text-xs font-semibold text-emerald-700">✓ Texted {fmtD(sentAt)}</span>;
  }
  return (
    <Button data-testid={`rebook-text-${row.appointment_id}`} size="sm" disabled={send.isPending || !row.phone}
      onClick={() => send.mutate()} className="h-7 bg-[#0B1F44] hover:bg-[#13306a] text-white text-xs">
      <MessageSquare className="h-3.5 w-3.5 mr-1" />{send.isPending ? "Sending…" : "Text to rebook"}
    </Button>
  );
}

export const KpiDrilldown = ({ kind, onClose }) => {
  const cfg = CONFIG[kind];
  const { data, isLoading } = useQuery({
    queryKey: ["kpi-detail", kind],
    queryFn: async () => (await api.get("/dashboard/stats", { params: { detail: kind } })).data,
    enabled: !!kind,
  });

  const exportCsv = () => {
    const lines = [cfg.cols.map((c) => csvCell(c[1])).join(",")];
    data.rows.forEach((r) => lines.push(cfg.cols.map(([k, , f]) => csvCell(f ? f(r[k], r) : r[k])).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={!!kind} onOpenChange={(o) => { if (!o) onClose(); }}>
      {cfg && (
        <DialogContent data-testid={`kpi-drilldown-${kind}`} className="sm:max-w-6xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">{cfg.title}</DialogTitle>
            <DialogDescription>{cfg.desc}</DialogDescription>
          </DialogHeader>
          {isLoading || !data ? <div className="py-10 text-center text-sm text-slate-400">Loading…</div> : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600" data-testid="kpi-drilldown-summary">
                <Summary kind={kind} data={data} />
                <Button data-testid="kpi-drilldown-export" size="sm" variant="outline" onClick={exportCsv} disabled={!data.rows.length}>
                  <Download className="h-4 w-4 mr-1.5" />Export CSV
                </Button>
              </div>
              <div className="overflow-auto border border-slate-100 rounded-lg">
                <table className="w-full text-sm" data-testid="kpi-drilldown-table">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 sticky top-0">
                    <tr>{cfg.cols.map((c) => <th key={c[0]} className="text-left px-3 py-2 whitespace-nowrap">{c[1]}</th>)}{kind === "no_shows" && <th className="px-3 py-2" />}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.rows.map((r, i) => (
                      <tr key={i} data-testid={`kpi-drilldown-row-${i}`} className={r.stage === "converted" ? "bg-emerald-50" : ""}>
                        {cfg.cols.map(([k, , f]) => (
                          <td key={k} data-testid={k === "rebooked_for" ? `rebooked-${r.appointment_id}` : undefined}
                            className={`px-3 py-2 whitespace-nowrap capitalize-first ${k === "rebooked_for" && r[k] ? "text-emerald-700 font-semibold" : "text-slate-700"}`}>
                            {f ? f(r[k], r) : r[k]}
                          </td>
                        ))}
                        {kind === "no_shows" && <td className="px-3 py-2 text-right"><RebookButton row={r} /></td>}
                      </tr>
                    ))}
                    {!data.rows.length && <tr><td colSpan={cfg.cols.length} className="px-3 py-8 text-center text-slate-400">Nothing to show for this period.</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};
